import { useMemo, useState } from 'react';
import {
  Calendar,
  ExternalLink,
  FileSignature,
  Info,
  Loader2,
  ShieldCheck,
  Wrench,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionCard } from '@/components/layout/SectionCard';
import { formatUsd } from '@/lib/format';
import { useCustomer } from '@/hooks/useCustomer';
import {
  useEquipmentAssets,
  useEquipmentContracts,
  type EquipmentAssetRow,
  type EquipmentContractRow,
} from '@/hooks/useEquipmentRentals';
import { useCreateServiceRequest } from '@/hooks/useServiceRequests';
import { fmtMirrorDate } from '@/lib/dates';

import { Loader2 as ChainSwitchSpinner } from 'lucide-react';
import { useGroupAssets } from '@/hooks/useGroupAssets';
import ChainEquipment from '@/pages/chain/ChainEquipment';

/**
 * /equipment — full list of leased / rented equipment with image, serial,
 * make/model, lease terms, preventative-maintenance guidance, and two
 * actions per asset:
 *
 *   • "Service this equipment" — opens a pre-filled service-ticket modal
 *     (kind=equipment_issue, subject + body populated with asset context)
 *     so the dispatch team gets the SN / model up front.
 *
 *   • "Review contract" — toggles a contract details panel when the asset
 *     is linked to a contract row.
 */
function SingleEquipment() {
  const { data: assets, isLoading: assetsLoading } = useEquipmentAssets();
  const { data: contracts } = useEquipmentContracts();
  const [serviceFor, setServiceFor] = useState<EquipmentAssetRow | null>(null);
  const [expandedContract, setExpandedContract] = useState<string | null>(null);

  const contractById = useMemo(() => {
    const m = new Map<string, EquipmentContractRow>();
    for (const c of contracts ?? []) m.set(c.id, c);
    return m;
  }, [contracts]);

  const visibleAssets = useMemo(
    () => (assets ?? []).filter((a) => a.status !== 'retired'),
    [assets],
  );
  const totalMonthly = visibleAssets
    .filter((a) => !a.isLoaner)
    .reduce((sum, a) => sum + a.monthlyRent, 0);

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-20 md:pb-10">
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-6 pb-12 space-y-6">
        <SectionCard
          icon={<Wrench size={20} />}
          title="Equipment"
          subtitle="Every piece of dispensing, refrigeration, or ice equipment Brix Beverage has on rent at your locations. Tap a unit for spec sheets, contract details, or to open a service ticket."
        >
          {assetsLoading ? (
            <div className="flex items-center gap-2 text-base text-slate-900 tracking-tight">
              <Loader2 size={15} className="animate-spin" /> Loading equipment…
            </div>
          ) : visibleAssets.length === 0 ? (
            <div className="text-center py-6">
              <Info size={20} className="text-slate-900 mx-auto mb-2" />
              <p className="text-base text-slate-900 tracking-tight leading-relaxed max-w-md mx-auto">
                No equipment on file yet. As soon as a contract is signed
                and the asset is installed, it'll show up here with its
                photo, serial, and service options.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 flex items-baseline justify-between gap-3 border-b border-brix-border pb-3">
                <div>
                  <div className="text-xs tracking-tight text-slate-900 font-semibold">
                    Units on rent
                  </div>
                  <div
                    className="font-mono text-2xl font-bold text-slate-900 tracking-tight leading-none mt-1"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {visibleAssets.filter((a) => !a.isLoaner).length}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs tracking-tight text-slate-900 font-semibold">
                    Monthly rent total
                  </div>
                  <div
                    className="font-mono text-2xl font-bold text-brix-primary tracking-tight leading-none mt-1"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatUsd(totalMonthly)}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {visibleAssets.map((asset) => {
                  const contract = asset.contractId
                    ? contractById.get(asset.contractId) ?? null
                    : null;
                  return (
                    <EquipmentCard
                      key={asset.id}
                      asset={asset}
                      contract={contract}
                      contractOpen={
                        contract !== null && expandedContract === contract.id
                      }
                      onToggleContract={() => {
                        if (!contract) return;
                        setExpandedContract((cur) =>
                          cur === contract.id ? null : contract.id,
                        );
                      }}
                      onServiceClick={() => setServiceFor(asset)}
                    />
                  );
                })}
              </div>
            </>
          )}
        </SectionCard>
      </div>

      {serviceFor && (
        <ServiceTicketModal
          asset={serviceFor}
          contract={
            serviceFor.contractId
              ? contractById.get(serviceFor.contractId) ?? null
              : null
          }
          onClose={() => setServiceFor(null)}
        />
      )}
    </div>
  );
}

/* ----------------------------- per-asset card ----------------------------- */

function EquipmentCard({
  asset,
  contract,
  contractOpen,
  onToggleContract,
  onServiceClick,
}: {
  asset: EquipmentAssetRow;
  contract: EquipmentContractRow | null;
  contractOpen: boolean;
  onToggleContract: () => void;
  onServiceClick: () => void;
}) {
  const title =
    asset.catalogName ??
    asset.qboItemName ??
    asset.description ??
    asset.assetTag ??
    'Equipment unit';
  const makeModel = [asset.make, asset.modelNumber].filter(Boolean).join(' · ');
  const pm = preventativeMaintenance(asset);

  return (
    <article className="rounded-xl border border-brix-border bg-brix-surface overflow-hidden shadow-card">
      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-0">
        {/* Equipment image */}
        <div className="bg-brix-surface-alt/40 border-b md:border-b-0 md:border-r border-brix-border flex items-center justify-center p-4">
          {asset.imageUrl ? (
            // External CDN image — webstaurantstore / katomcdn are public.
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={asset.imageUrl}
              alt={title}
              className="max-h-32 md:max-h-40 w-auto object-contain"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-32 md:h-40 flex items-center justify-center text-slate-400">
              <Wrench size={42} />
            </div>
          )}
        </div>

        {/* Detail panel */}
        <div className="p-5 space-y-3">
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900 tracking-tight leading-tight">
              {title}
            </h3>
            {makeModel && (
              <p className="text-sm text-slate-900 tracking-tight mt-0.5">
                {makeModel}
              </p>
            )}
            {asset.isLoaner && (
              <span className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                <Info size={11} /> Loaner · no monthly charge
              </span>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            {asset.serialNumber && (
              <DescItem
                label="Serial"
                value={
                  <span className="font-mono">{asset.serialNumber}</span>
                }
              />
            )}
            {asset.assetTag && (
              <DescItem
                label="Asset tag"
                value={<span className="font-mono">{asset.assetTag}</span>}
              />
            )}
            {asset.installedAt && (
              <DescItem
                label="Installed"
                value={fmtDate(asset.installedAt)}
              />
            )}
            {!asset.isLoaner && (
              <DescItem
                label="Monthly rent"
                value={
                  <span
                    className="font-mono font-bold"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatUsd(asset.monthlyRent)}
                  </span>
                }
              />
            )}
            {contract?.contractNumber && (
              <DescItem
                label="Contract"
                value={
                  <span className="font-mono">{contract.contractNumber}</span>
                }
              />
            )}
            {contract?.startDate && contract.endDate && (
              <DescItem
                label="Lease term"
                value={
                  <>
                    {fmtDate(contract.startDate)} → {fmtDate(contract.endDate)}
                  </>
                }
              />
            )}
          </dl>

          {pm && (
            <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2.5 text-xs text-slate-900 tracking-tight leading-snug flex gap-2 items-start">
              <ShieldCheck size={13} className="text-emerald-700 mt-0.5 shrink-0" />
              <span>
                <strong>Preventative maintenance:</strong> {pm}
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={onServiceClick}
            >
              <Wrench size={13} /> Service this equipment
            </Button>
            {contract && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={onToggleContract}
              >
                <FileSignature size={13} />
                {contractOpen ? 'Hide contract' : 'Review contract'}
              </Button>
            )}
            {asset.specSheetUrl && (
              <a
                href={asset.specSheetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brix-border bg-brix-surface text-brix-primary font-semibold text-xs hover:bg-brix-surface-alt"
              >
                <ExternalLink size={11} /> Spec sheet
              </a>
            )}
          </div>
        </div>
      </div>

      {contractOpen && contract && (
        <ContractPanel contract={contract} />
      )}
    </article>
  );
}

function DescItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs tracking-tight text-slate-500 font-semibold">
        {label}
      </dt>
      <dd className="text-sm text-slate-900 tracking-tight truncate">{value}</dd>
    </div>
  );
}

function ContractPanel({ contract }: { contract: EquipmentContractRow }) {
  const monthlyTotal =
    (contract.monthlyEquipmentAmount ?? 0) +
    (contract.monthlyServiceAmount ?? 0);
  const isSigned = contract.signatureStatus === 'signed';
  return (
    <div className="border-t border-brix-border bg-brix-surface-alt/40 px-5 py-4 space-y-3">
      <div className="flex flex-wrap items-baseline gap-3 justify-between">
        <div>
          <div className="text-xs tracking-tight text-slate-500 font-semibold">
            Contract details
          </div>
          <div className="font-mono text-sm font-bold text-slate-900 tracking-tight">
            {contract.contractNumber ?? '—'}
          </div>
        </div>
        {isSigned && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <FileSignature size={9} /> Signed
            {contract.signedAt && (
              <span className="ml-1 text-emerald-700/70">
                {fmtDate(contract.signedAt)}
              </span>
            )}
          </span>
        )}
      </div>

      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm">
        {contract.contractType && (
          <DescItem
            label="Type"
            value={contract.contractType}
          />
        )}
        {contract.category && (
          <DescItem label="Category" value={contract.category} />
        )}
        {contract.status && (
          <DescItem label="Status" value={contract.status} />
        )}
        {contract.startDate && (
          <DescItem
            label="Start date"
            value={fmtDate(contract.startDate)}
          />
        )}
        {contract.endDate && (
          <DescItem
            label="End date"
            value={fmtDate(contract.endDate)}
          />
        )}
        {monthlyTotal > 0 && (
          <DescItem
            label="Monthly total"
            value={
              <span
                className="font-mono font-bold"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatUsd(monthlyTotal)}
              </span>
            }
          />
        )}
      </dl>

      {contract.externalDocumentUrl && (
        <a
          href={contract.externalDocumentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-brix-primary font-semibold text-xs underline"
        >
          <ExternalLink size={11} /> Open signed contract document
        </a>
      )}
    </div>
  );
}

/* ------------------------- service ticket modal ------------------------- */

function ServiceTicketModal({
  asset,
  contract,
  onClose,
}: {
  asset: EquipmentAssetRow;
  contract: EquipmentContractRow | null;
  onClose: () => void;
}) {
  const { data: ctx } = useCustomer();
  const create = useCreateServiceRequest();
  const equipmentTitle =
    asset.catalogName ??
    asset.qboItemName ??
    asset.description ??
    asset.assetTag ??
    'Equipment unit';

  const defaultSubject = `Service request — ${equipmentTitle}${
    asset.serialNumber ? ` (SN ${asset.serialNumber})` : ''
  }`;
  const defaultBody = [
    asset.make && asset.modelNumber
      ? `Make/model: ${asset.make} ${asset.modelNumber}`
      : null,
    asset.serialNumber ? `Serial: ${asset.serialNumber}` : null,
    asset.assetTag ? `Asset tag: ${asset.assetTag}` : null,
    asset.installedAt ? `Installed: ${fmtDate(asset.installedAt)}` : null,
    contract?.contractNumber ? `Contract: ${contract.contractNumber}` : null,
    '',
    'Issue description:',
    '',
  ]
    .filter((l) => l !== null)
    .join('\n');

  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!ctx) return;
    if (!subject.trim()) return;
    create.mutate(
      {
        customerId: ctx.customer.id,
        locationId: ctx.locations[0]?.id ?? null,
        kind: 'equipment_issue',
        subject,
        body,
      },
      { onSuccess: onClose },
    );
  }

  const errMsg = create.error instanceof Error ? create.error.message : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="equipment-svc-heading"
      className="fixed inset-0 z-[110] flex items-start md:items-center justify-center px-4 py-8 overflow-y-auto"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-card-deep overflow-hidden border-2 border-brix-primary-deep">
        <div className="bg-brix-primary-deep px-6 py-4 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-white shrink-0">
              <Wrench size={18} />
            </div>
            <div className="min-w-0">
              <h2
                id="equipment-svc-heading"
                className="font-display text-lg font-bold tracking-tight text-white leading-none"
              >
                Service this equipment
              </h2>
              <p className="text-xs text-white/85 tracking-tight mt-1 truncate">
                {equipmentTitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-white hover:bg-white/15 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="svc-subject">Subject</Label>
            <Input
              id="svc-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={140}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="svc-body">What's going on?</Label>
            <textarea
              id="svc-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              maxLength={2000}
              className="w-full rounded-md border border-brix-border bg-brix-surface px-3 py-2 text-sm font-mono leading-relaxed placeholder:text-brix-muted/70 focus-visible:outline-none focus-visible:border-brix-primary focus-visible:ring-2 focus-visible:ring-brix-primary/20"
            />
          </div>
          {errMsg && (
            <div className="text-xs text-red-600 tracking-tight px-3 py-2 rounded-md border border-red-200 bg-red-50">
              {errMsg}
            </div>
          )}
          <p className="text-[11px] text-slate-900/70 tracking-tight italic flex items-start gap-1.5">
            <Calendar size={11} className="mt-0.5 shrink-0" />
            The Brix service team gets an email the moment you submit — same
            channel as the New ticket form on Home.
          </p>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={create.isPending || !subject.trim()}>
              {create.isPending ? <Loader2 size={13} className="animate-spin" /> : <Wrench size={13} />}
              {create.isPending ? 'Submitting…' : 'Submit service ticket'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------- helpers ------------------------------- */

function fmtDate(iso: string): string {
  return fmtMirrorDate(iso);
}

/**
 * Preventative-maintenance copy keyed off equipment category. Plain-English
 * cadence that matches the field manuals + the useful_life_months from the
 * catalog. Conservative — when in doubt we say "have us inspect annually".
 */
function preventativeMaintenance(asset: EquipmentAssetRow): string | null {
  const cat = (asset.category ?? '').toLowerCase();
  if (cat === 'ice_machine') {
    return 'Every 6 months — descale the evaporator, sanitize the bin and water lines, change the inlet water filter. Annual condenser coil cleaning.';
  }
  if (cat === 'refrigeration') {
    return 'Quarterly — clean the condenser coil and door gaskets, verify holding temperature. Annual full PM with refrigerant + electrical check.';
  }
  if (cat === 'fountain') {
    return 'Monthly — sanitize nozzles and check BIB connections. Quarterly carbonator + regulator inspection. Annual deep clean of cold plate.';
  }
  if (cat === 'bargun') {
    return 'Monthly — remove and sanitize the spout/diffuser, flush syrup lines, check holster CO2 lines. Annual inspection of the manifold and check valves.';
  }
  if (cat === 'freshpet') {
    return 'Per Fresh Pet field manual — quarterly compressor + door seal check, annual refrigerant and electronic-controls inspection.';
  }
  if (cat === 'service_contract') {
    return 'Annual on-site service visit covers full PM under your service contract.';
  }
  if (cat === 'warehouse') {
    return 'Annual inspection — verify operating condition, safety items, and any required certifications.';
  }
  if (cat === 'cooking') {
    return 'Every 6 months — clean burners/heating elements, inspect gas connections and venting, verify thermostat calibration.';
  }
  return null;
}

/**
 * A chain's corporate login standing on the master gets EVERY store's equipment
 * instead of the master account's own handful. Everyone else — a store's own
 * people, and the same person once they pick a store — gets the page above,
 * unchanged.
 *
 * ⚠ A WRAPPER, not a branch inside the page. Returning early above that
 * component's own hooks would change hook order between renders.
 */
export default function Equipment() {
  const { data: group, isLoading } = useGroupAssets();

  // Hold the single-account render until we know which page this is. A flash
  // of corporate's own empty equipment before the chain view lands reads as
  // "where did everything go".
  if (isLoading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-brix-muted">
        <ChainSwitchSpinner size={16} className="animate-spin inline-block mr-2" />
        Loading your equipment…
      </div>
    );
  }
  if (group?.is_group) return <ChainEquipment />;
  return <SingleEquipment />;
}
